/**
 * Public API surface for @cross-deck/web.
 *
 * Usage (browser):
 *
 *   import { Crossdeck } from "@cross-deck/web";
 *
 *   Crossdeck.init({
 *     appId: "app_web_xxx",
 *     publicKey: "cd_pub_live_…",
 *     environment: "production",
 *   });
 *
 *   await Crossdeck.identify("user_847");
 *   const ents = await Crossdeck.getEntitlements();
 *   if (Crossdeck.isEntitled("pro")) {
 *     showPro();
 *   }
 *   Crossdeck.track("paywall_shown", { variant: "v3" });
 *
 *
 * Usage (Node):
 *
 *   import { Crossdeck, MemoryStorage } from "@cross-deck/web";
 *
 *   Crossdeck.init({
 *     appId: "app_node_xxx",
 *     publicKey: "cd_pub_test_…",
 *     environment: "sandbox",
 *     storage: new MemoryStorage(),  // session-only persistence
 *     autoHeartbeat: false,           // skip the boot ping in scripts
 *   });
 */

import { CrossdeckError } from "./errors";
import { HttpClient, SDK_NAME, SDK_VERSION, DEFAULT_BASE_URL } from "./http";
import { IdentityStore } from "./identity";
import { EntitlementCache, type EntitlementsListener } from "./entitlement-cache";
import { EventQueue, type QueuedEvent } from "./event-queue";
import { PersistentEventStore } from "./event-storage";
import { CookieStorage, detectDefaultStorage, MemoryStorage } from "./storage";
import { randomChars } from "./identity";
import { collectDeviceInfo, type DeviceInfo } from "./device-info";
import { AutoTracker, DEFAULT_AUTO_TRACK, type AutoTrackConfig } from "./auto-track";
import { ConsoleDebugLogger, findSensitivePropertyKeys, type DebugLogger } from "./debug";
import { validateEventProperties } from "./event-validation";
import { SuperPropertyStore } from "./super-properties";
import { WebVitalsTracker } from "./web-vitals";
import { ConsentManager, scrubPii, scrubPiiFromProperties, type ConsentState } from "./consent";
import type {
  AliasResult,
  AutoTrackOptions,
  CrossdeckOptions,
  Diagnostics,
  EntitlementsListResponse,
  Environment,
  EventProperties,
  GroupTraits,
  HeartbeatResponse,
  IdentifyOptions,
  PublicEntitlement,
  PurchaseResult,
} from "./types";

interface InternalState {
  http: HttpClient;
  identity: IdentityStore;
  entitlements: EntitlementCache;
  events: EventQueue;
  autoTracker: AutoTracker | null;
  webVitals: WebVitalsTracker | null;
  superProps: SuperPropertyStore;
  consent: ConsentManager;
  scrubPii: boolean;
  /** Cached enrichment payload merged into every event's properties. */
  deviceInfo: DeviceInfo;
  options: Required<
    Omit<
      CrossdeckOptions,
      "storage" | "sdkVersion" | "autoTrack" | "appVersion" | "debug" | "respectDnt" | "scrubPii"
    >
  > & {
    sdkVersion: string;
    autoTrack: AutoTrackConfig;
    appVersion: string | null;
  };
  debug: DebugLogger;
  developerUserId: string | null;
  /** Cleanup the unload-flush listeners installed in init(). */
  uninstallUnloadFlush: (() => void) | null;
  /** Most-recent server time observed via heartbeat (epoch ms). */
  lastServerTime: number | null;
  /** Local Date.now() captured at the same moment as lastServerTime. */
  lastClientTime: number | null;
}

export class CrossdeckClient {
  private state: InternalState | null = null;

  /**
   * Boot the SDK. Idempotent — calling init twice with the same options
   * is a no-op; calling with different options replaces the previous
   * configuration.
   *
   * NorthStar §11.1: signature is `Crossdeck.init({ appId, publicKey,
   * environment })`. The trio is validated up-front so a typo'd key or a
   * mismatched env fails fast at boot rather than at first event-flush.
   */
  init(options: CrossdeckOptions): void {
    if (!options.publicKey || !options.publicKey.startsWith("cd_pub_")) {
      throw new CrossdeckError({
        type: "configuration_error",
        code: "invalid_public_key",
        message: "Crossdeck.init requires a publishable key starting with cd_pub_.",
      });
    }
    if (!options.appId) {
      throw new CrossdeckError({
        type: "configuration_error",
        code: "missing_app_id",
        message: "Crossdeck.init requires an appId. Find yours in the Crossdeck dashboard.",
      });
    }
    if (options.environment !== "production" && options.environment !== "sandbox") {
      throw new CrossdeckError({
        type: "configuration_error",
        code: "invalid_environment",
        message: 'Crossdeck.init requires environment: "production" | "sandbox".',
      });
    }
    // Key prefix must match the declared environment, otherwise prod
    // telemetry could silently route into sandbox dashboards (or vice
    // versa). NorthStar §15 calls this out as a "fail loudly" condition.
    const keyEnv = inferEnvFromKey(options.publicKey);
    if (keyEnv && keyEnv !== options.environment) {
      throw new CrossdeckError({
        type: "configuration_error",
        code: "environment_mismatch",
        message: `Crossdeck.init: environment "${options.environment}" disagrees with key prefix (${keyEnv}). Reconcile the publishable key with the environment declaration.`,
      });
    }

    // Localhost auto-detection. When the SDK boots from localhost /
    // 127.0.0.1 / *.local / RFC1918 private IPs, automatically switch
    // to a fully-local "dev mode" — no network calls fire, all SDK
    // methods (track, identify, isEntitled) work against in-memory +
    // localStorage state only. The dev's live dashboard stays clean
    // even if they forgot to swap their cd_pub_live_* key for a
    // cd_pub_test_* one.
    //
    // Stripe-grade default. Confidence-first means we trust the dev's
    // key prefix in production; localhost is the one place where we
    // proactively prevent accidental pollution.
    const localDevMode = isLocalHostname();

    const storage = options.storage ?? detectDefaultStorage();
    const persistIdentity = options.persistIdentity ?? true;
    const autoTrack = resolveAutoTrack(options.autoTrack);
    const opts: InternalState["options"] = {
      appId: options.appId,
      publicKey: options.publicKey,
      environment: options.environment,
      baseUrl: options.baseUrl ?? DEFAULT_BASE_URL,
      persistIdentity,
      storagePrefix: options.storagePrefix ?? "crossdeck:",
      autoHeartbeat: options.autoHeartbeat ?? true,
      eventFlushBatchSize: options.eventFlushBatchSize ?? 20,
      // 1500ms idle window. Short enough that an event queued on page
      // load still flushes if the user leaves quickly (the keepalive
      // pagehide handler picks up anything that doesn't); long enough
      // that bursts of clicks coalesce into one network round-trip.
      eventFlushIntervalMs: options.eventFlushIntervalMs ?? 1500,
      sdkVersion: options.sdkVersion ?? SDK_VERSION,
      autoTrack,
      appVersion: options.appVersion ?? null,
    };

    const debug = new ConsoleDebugLogger();
    debug.enabled = options.debug === true;

    const http = new HttpClient({
      publicKey: opts.publicKey,
      baseUrl: opts.baseUrl,
      sdkVersion: opts.sdkVersion,
      // Localhost auto-route: HttpClient short-circuits every request
      // to a successful no-op response when localDevMode is set.
      // SDK methods continue to work locally; nothing reaches the
      // server.
      localDevMode,
    });

    if (localDevMode) {
      // Single console line on first init — direct, not scolding.
      // Tells the dev exactly what's happening and how to change it.
      console.log(
        "[crossdeck] Localhost detected — running in dev mode (no network calls). " +
        "Set publicKey: 'cd_pub_test_…' and deploy to a real domain to test against the Crossdeck Sandbox.",
      );
    }
    // Bank-grade identity continuity (v0.6.0+). When persistIdentity is
    // on AND we're in a browser, the SDK writes the anonymousId to BOTH
    // localStorage (primary) and a 1st-party cookie (secondary). When
    // persistIdentity is off — typical during a strict-consent flow
    // before opt-in — we fall back to in-memory only and write nothing
    // to either store.
    //
    // The cookie is only constructed when the caller didn't override
    // `storage`; if a custom storage adapter was supplied, that wins
    // and the cookie redundancy is the caller's responsibility (they
    // chose a non-default store for a reason).
    const effectiveStorage = persistIdentity ? storage : new MemoryStorage();
    const useCookieRedundancy =
      persistIdentity &&
      !options.storage &&  // honour caller's adapter choice
      typeof (globalThis as { document?: unknown }).document !== "undefined";
    const cookieStore = useCookieRedundancy ? new CookieStorage() : undefined;
    const identity = new IdentityStore(effectiveStorage, opts.storagePrefix, cookieStore);
    const entitlements = new EntitlementCache();
    // Durable persistence — write queued events through to the primary
    // identity store (typically localStorage) so a crash / hard close /
    // keepalive cap exceedance doesn't lose data. Skipped when
    // persistIdentity is off (strict consent / in-memory-only mode) —
    // no point writing events to a store the developer told us not to
    // use.
    const persistentEvents = persistIdentity
      ? new PersistentEventStore({ storage: effectiveStorage, prefix: opts.storagePrefix })
      : null;
    if (persistentEvents) {
      debug.emit(
        "sdk.queue_restored",
        "Restored persisted event queue from a prior session.",
      );
    }
    const events = new EventQueue({
      http,
      batchSize: opts.eventFlushBatchSize,
      intervalMs: opts.eventFlushIntervalMs,
      envelope: () => ({
        appId: opts.appId,
        environment: opts.environment,
        sdk: { name: SDK_NAME, version: opts.sdkVersion },
      }),
      persistentStore: persistentEvents ?? undefined,
      onFirstFlushSuccess: () => {
        debug.emit(
          "sdk.first_event_sent",
          "First telemetry event received. View it in Live Events.",
          { appId: opts.appId, environment: opts.environment },
        );
      },
      onRetryScheduled: (info) => {
        debug.emit(
          "sdk.flush_retry_scheduled",
          `Event flush failed (${info.lastError}). Retrying in ${info.delayMs}ms (attempt ${info.consecutiveFailures}).`,
          { ...info },
        );
      },
    });

    // Collect device info ONCE at boot; cheap to re-use on every event.
    const deviceInfo: DeviceInfo = autoTrack.deviceInfo
      ? collectDeviceInfo({ appVersion: opts.appVersion ?? undefined })
      : opts.appVersion
        ? { appVersion: opts.appVersion }
        : {};

    // Super-property + groups store — Mixpanel pattern. Lives on the
    // primary identity storage so it survives page reloads but is
    // cleared on reset() / forget(). Skipped when persistIdentity is
    // off (strict consent — no writes anywhere).
    const superProps = new SuperPropertyStore(
      persistIdentity ? effectiveStorage : new MemoryStorage(),
      opts.storagePrefix,
    );

    // Consent gating. DNT auto-detection runs once here if respectDnt
    // is enabled; otherwise the developer is responsible for calling
    // Crossdeck.consent({...}) before user-meaningful events fire.
    const consent = new ConsentManager({ respectDnt: options.respectDnt === true });
    if (consent.isDntDenied) {
      debug.emit(
        "sdk.consent_dnt_applied",
        "Do Not Track detected — all tracking dimensions denied at init.",
      );
    }

    this.state = {
      http,
      identity,
      entitlements,
      events,
      autoTracker: null,
      webVitals: null,
      superProps,
      consent,
      scrubPii: options.scrubPii !== false,
      deviceInfo,
      options: opts,
      debug,
      developerUserId: null,
      uninstallUnloadFlush: null,
      lastServerTime: null,
      lastClientTime: null,
    };

    debug.emit("sdk.configured", `Crossdeck connected to ${opts.appId} in ${opts.environment} mode.`, {
      appId: opts.appId,
      environment: opts.environment,
      sdkVersion: opts.sdkVersion,
    });

    // Auto-tracker boots AFTER state is set so its initial track() calls
    // can resolve identity hints and device-info enrichment correctly.
    if (autoTrack.sessions || autoTrack.pageViews) {
      const tracker = new AutoTracker(autoTrack, (name, properties) =>
        this.track(name, properties),
      );
      this.state.autoTracker = tracker;
      tracker.install();
    }
    // Web Vitals tracker — emits LCP / INP / CLS / FCP / TTFB as named
    // events. No-op in non-browser environments or when the
    // PerformanceObserver primitive is missing.
    if (autoTrack.webVitals) {
      const vitals = new WebVitalsTracker(
        { enabled: true },
        (name, properties) => this.track(name, properties),
      );
      this.state.webVitals = vitals;
      vitals.install();
    }

    // Terminal flush wiring — without this, every page navigation drops
    // whatever's queued (page.viewed on load, session.ended on pagehide,
    // user clicks within the idle window). Use keepalive so the request
    // survives the unload. visibilitychange→hidden is the canonical
    // mobile signal (pagehide also fires there); pagehide + beforeunload
    // are the desktop ones. We listen to all three and rely on the
    // queue being a no-op when empty so a single trigger flushes once.
    this.state.uninstallUnloadFlush = installUnloadFlush(() => {
      // Fire-and-forget. Errors here can't be handled meaningfully — the
      // page is going away. Keepalive lets the browser keep the request
      // alive past unload up to 64 KB total in flight.
      void this.flush({ keepalive: true }).catch(() => undefined);
    });

    if (opts.autoHeartbeat && !localDevMode) {
      // Fire-and-forget — heartbeat failure shouldn't block init().
      // Skipped in dev mode — there's nothing to heartbeat.
      void this.heartbeat().catch(() => undefined);
    }
  }

  /**
   * @deprecated Use `init()` instead. NorthStar §4 standardised the
   * lifecycle method name across SDKs as `init` (formerly `start` /
   * `configure`). `start` will be removed in a future major version.
   */
  start(options: CrossdeckOptions): void {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        "[crossdeck] Crossdeck.start() is deprecated — use Crossdeck.init() instead. The signature is the same.",
      );
    }
    this.init(options);
  }

  /**
   * Link the anonymous device to a developer-supplied user ID. Cache
   * the resolved Crossdeck customer for follow-up calls.
   *
   * v0.9.0+ accepts an optional `traits` bag — profile data (name,
   * plan, signupDate, role) persisted on the Crossdeck customer record
   * and queryable from dashboards. Traits are sanitised through the
   * same validator that gates `track()` properties, so a `{ avatar:
   * <File>, onSave: () => {} }` payload can't corrupt the alias call.
   *
   *   Crossdeck.identify("user_847", {
   *     email: "wes@pinet.co.za",
   *     traits: { name: "Wes", plan: "pro", signedUpAt: "2026-05-11" },
   *   });
   */
  async identify(userId: string, options?: IdentifyOptions): Promise<AliasResult> {
    const s = this.requireStarted();
    if (!userId) {
      throw new CrossdeckError({
        type: "invalid_request_error",
        code: "missing_user_id",
        message: "identify(userId) requires a non-empty userId.",
      });
    }
    if (!s.consent.analytics) {
      // No-op on consent denial — but throw NOT — the developer
      // expected an aliasResult to await. Return a no-op result that
      // mirrors the wire shape so existing call chains don't break.
      s.debug.emit(
        "sdk.consent_denied",
        `identify() skipped — consent denied for analytics.`,
      );
      return {
        object: "alias_result",
        crossdeckCustomerId: s.identity.crossdeckCustomerId ?? "",
        linked: [],
        mergePending: false,
        env: s.options.environment,
      };
    }
    // Sanitise traits at the SDK boundary so a malformed bag (function,
    // BigInt, circular) never crashes the alias request. Empty result
    // → omit the field entirely so backends that don't yet know about
    // traits aren't surprised by an unknown key.
    const traitsValidation =
      options?.traits !== undefined
        ? validateEventProperties(options.traits)
        : null;
    const traits = traitsValidation && Object.keys(traitsValidation.properties).length > 0
      ? traitsValidation.properties
      : undefined;
    if (s.debug.enabled && traitsValidation && traitsValidation.warnings.length > 0) {
      for (const w of traitsValidation.warnings) {
        s.debug.emit(
          "sdk.property_coerced",
          `identify() traits key ${JSON.stringify(w.key)} was ${w.kind.replace(/_/g, " ")} during validation.`,
          { key: w.key, kind: w.kind },
        );
      }
    }
    const body: Record<string, unknown> = {
      userId,
      anonymousId: s.identity.anonymousId,
    };
    if (options?.email) body.email = options.email;
    if (traits) body.traits = traits;
    const result = await s.http.request<AliasResult>("POST", "/identity/alias", {
      body,
    });
    s.identity.setCrossdeckCustomerId(result.crossdeckCustomerId);
    s.developerUserId = userId;
    return result;
  }

  /**
   * Register super-properties — Mixpanel pattern. Once set, every
   * subsequent event of THIS SDK instance carries these keys on its
   * properties bag automatically.
   *
   *   Crossdeck.register({ plan: "pro", releaseChannel: "beta" });
   *   Crossdeck.track("paywall_shown");  // includes plan + releaseChannel
   *
   * Values that are `null` are deleted (the explicit "stop tracking
   * this key" idiom). Returns the resulting bag.
   *
   * Sanitised through `validateEventProperties` so a `{ avatar: File }`
   * payload can't poison the queue at flush time.
   */
  register(properties: Record<string, unknown>): Record<string, unknown> {
    const s = this.requireStarted();
    const validation = validateEventProperties(properties);
    return s.superProps.register(validation.properties);
  }

  /** Remove a single super-property key. Idempotent. */
  unregister(key: string): void {
    const s = this.requireStarted();
    s.superProps.unregister(key);
  }

  /** Snapshot of the current super-property bag. */
  getSuperProperties(): Record<string, unknown> {
    if (!this.state) return {};
    return this.state.superProps.getSuperProperties();
  }

  /**
   * Associate the current user with a group (org, team, account, etc.).
   * Mixpanel / Segment "Group Analytics" pattern.
   *
   *   Crossdeck.group("org", "acme_inc");
   *   Crossdeck.group("team", "design", { headcount: 12 });
   *
   * Once set, every subsequent event carries `$groups.<type>: id` on
   * its properties bag, enabling B2B dashboards ("how is Acme using
   * the product"). Pass `id: null` to clear a group membership.
   */
  group(type: string, id: string | null, traits?: GroupTraits): void {
    const s = this.requireStarted();
    if (!type) {
      throw new CrossdeckError({
        type: "invalid_request_error",
        code: "missing_group_type",
        message: "group(type, id) requires a non-empty type.",
      });
    }
    const sanitisedTraits = traits ? validateEventProperties(traits).properties : undefined;
    s.superProps.setGroup(type, id, sanitisedTraits);
  }

  /** Snapshot of the current groups map keyed by type. */
  getGroups(): Record<string, { id: string; traits?: Record<string, unknown> }> {
    if (!this.state) return {};
    return this.state.superProps.getGroups();
  }

  /**
   * Update consent state. Three independent dimensions:
   *
   *   analytics  — track() + identify() + auto-emissions
   *   marketing  — paid-traffic click IDs + referrer URL on events
   *   errors     — Web Vitals + (future) error reporting
   *
   * Each defaults to `true` (granted). Pass partial state — only the
   * keys you provide are changed.
   *
   *   Crossdeck.consent({ analytics: false });
   *   Crossdeck.consent({ marketing: true, errors: true });
   *
   * DNT-derived denies cannot be flipped back on; if the browser said
   * "don't track" we don't track even if the developer code disagrees.
   */
  consent(state: Partial<ConsentState>): ConsentState {
    const s = this.requireStarted();
    const next = s.consent.set(state);
    s.debug.emit("sdk.consent_changed", "Consent state updated.", { ...next });
    return next;
  }

  /** Snapshot of the current consent state. */
  consentStatus(): ConsentState {
    if (!this.state) {
      return { analytics: true, marketing: true, errors: true };
    }
    return this.state.consent.get();
  }

  /**
   * GDPR/CCPA "right to be forgotten" — calls the backend's
   * /v1/identity/forget endpoint to schedule a server-side deletion of
   * the customer's events and profile, then wipes all local state
   * (identity, entitlements, queue, super-props, persistent stores).
   *
   * Idempotent. Safe to call when no identity has been established
   * (it just wipes the empty local state).
   *
   * After forget() resolves, the SDK is in the same shape as if the
   * developer had called `Crossdeck.reset()` — a fresh anonymousId is
   * minted and the next session is a brand new identity-graph entry.
   */
  async forget(): Promise<void> {
    const s = this.requireStarted();
    const identityQuery = this.identityQueryParams();
    try {
      await s.http.request<{ object: "forgot" }>("POST", "/identity/forget", {
        body: {
          // Send every identity hint we hold; the server resolves the
          // canonical customer record and queues deletion. Missing
          // endpoint (older backend) gracefully degrades — local state
          // still wipes via the reset() call below.
          ...identityQuery,
        },
      });
    } catch (err) {
      // Server-side deletion failure is recorded but does not block
      // local wipe — the developer's user just asked to be forgotten,
      // refusing to clear their device because the backend hiccupped
      // would be the wrong call.
      s.debug.emit(
        "sdk.consent_denied",
        `forget() server call failed (${err instanceof Error ? err.message : String(err)}). Local state wiped anyway.`,
      );
    }
    this.reset();
  }

  /**
   * Read the current customer's active entitlements from the server.
   * Updates the local cache so subsequent isEntitled() calls answer
   * synchronously.
   */
  async getEntitlements(): Promise<PublicEntitlement[]> {
    const s = this.requireStarted();
    const query = this.identityQueryParams();
    const result = await s.http.request<EntitlementsListResponse>(
      "GET",
      "/entitlements",
      { query }
    );
    if (result.crossdeckCustomerId) {
      s.identity.setCrossdeckCustomerId(result.crossdeckCustomerId);
    }
    s.entitlements.setFromList(result.data);
    return result.data;
  }

  /**
   * Synchronous read from the local cache. Returns false if the cache
   * has never been populated (call getEntitlements first to warm it).
   */
  isEntitled(key: string): boolean {
    const s = this.requireStarted();
    return s.entitlements.isEntitled(key);
  }

  /** Snapshot of the local entitlement cache. */
  listEntitlements(): PublicEntitlement[] {
    const s = this.requireStarted();
    return s.entitlements.list();
  }

  /**
   * Subscribe to entitlement-cache changes. Returns an unsubscribe fn.
   *
   * The listener is invoked AFTER the cache mutates — once after a
   * successful `getEntitlements()` warms it, again after `syncPurchases()`
   * delivers fresh entitlements, and once on `reset()` to fire the
   * empty-cache state for logout flows.
   *
   * It is NOT invoked synchronously on subscribe. Callers that need
   * the current state should read it via `isEntitled()` / `listEntitlements()`
   * inline; the listener fires only on FUTURE changes.
   *
   * This is the foundation of the `useEntitlement` React hook in
   * `@cross-deck/web/react` — without it, React (or SwiftUI / Compose
   * / Vue) would have no way to re-render when entitlements arrive
   * asynchronously after init. The naive pattern of calling
   * `Crossdeck.isEntitled("pro")` directly inside a render path
   * shows the empty-cache result forever; binding the result to
   * component state via `onEntitlementsChange` is the correct
   * pattern.
   *
   * Idempotent unsubscribe — calling the returned function multiple
   * times is safe.
   *
   * Listener errors are swallowed (a buggy listener can't crash the
   * SDK or other listeners).
   */
  onEntitlementsChange(listener: EntitlementsListener): () => void {
    const s = this.requireStarted();
    return s.entitlements.subscribe(listener);
  }

  /**
   * Queue a telemetry event. Returns immediately — the network round-
   * trip happens in the background. To flush before the page unloads,
   * call flush().
   */
  track(name: string, properties?: EventProperties): void {
    const s = this.requireStarted();
    if (!name) {
      throw new CrossdeckError({
        type: "invalid_request_error",
        code: "missing_event_name",
        message: "track(name) requires a non-empty name.",
      });
    }

    // ----- Consent gate -----
    // Analytics consent is the gate for general track() calls. Web
    // Vitals events (`webvitals.*`) gate on the `errors` dimension
    // since they're treated as performance / reliability data rather
    // than behavioural analytics. Both gates default-on; the developer
    // must explicitly call Crossdeck.consent({...false}) to drop them.
    const isWebVital = name.startsWith("webvitals.");
    const consentGateOk = isWebVital ? s.consent.errors : s.consent.analytics;
    if (!consentGateOk) {
      if (s.debug.enabled) {
        s.debug.emit(
          "sdk.consent_denied",
          `Dropped event "${name}" — consent denied for ${isWebVital ? "errors" : "analytics"}.`,
        );
      }
      return;
    }

    // NorthStar §15: warn (in debug mode) when a property name looks
    // dangerously like PII — email/password/token/secret/card/phone.
    // We don't strip the field; that's the developer's call. We just
    // surface the signal so they can spot accidental leaks early.
    if (s.debug.enabled && properties) {
      const flagged = findSensitivePropertyKeys(properties);
      if (flagged.length > 0) {
        s.debug.emit(
          "sdk.sensitive_property_warning",
          `Event "${name}" has potentially sensitive property names: ${flagged.join(", ")}. Crossdeck is privacy-first — avoid sending PII unless intentional.`,
          { eventName: name, flagged },
        );
      }
    }

    // §16 "No identity" — only emit once per session so a chatty client
    // doesn't spam the log with every track() before identify().
    if (s.debug.enabled && !s.developerUserId && !s.identity.crossdeckCustomerId) {
      s.debug.emit(
        "sdk.no_identity",
        "Using anonymous user until identify(userId) is called.",
      );
    }

    // Validate + coerce caller-supplied properties BEFORE merging with
    // SDK enrichment. This is the boundary where untrusted developer
    // input becomes safe-to-serialise data: functions/symbols dropped,
    // Date / BigInt / Error coerced to JSON-friendly shapes, oversized
    // strings truncated, circular refs replaced. Without this, one bad
    // property (e.g. `{ onClick: () => {} }`) would crash JSON.stringify
    // at flush time and poison the entire batch indefinitely.
    //
    // The SDK's own enrichment (device info, sessionId, utm_*) is
    // trusted and not re-validated — those values are produced by
    // `collectDeviceInfo()` and `captureAcquisition()`, both of which
    // are typed and bounded.
    const validation = validateEventProperties(properties);
    if (s.debug.enabled && validation.warnings.length > 0) {
      for (const w of validation.warnings) {
        s.debug.emit(
          "sdk.property_coerced",
          `Event "${name}" property ${JSON.stringify(w.key)} was ${w.kind.replace(/_/g, " ")} during validation.`,
          { eventName: name, key: w.key, kind: w.kind },
        );
      }
    }

    // Enrichment policy: device info first, then auto-tracker context
    // (sessionId + per-session acquisition utm_*/referrer), then
    // caller-supplied properties last so a developer can override
    // anything the SDK auto-attached.
    //
    // Acquisition fields are session-scoped (captured once at session
    // start by AutoTracker) and attached to every event of that session
    // — that's the GA4 model: same source/medium/campaign labels every
    // event in the same visit. Empty strings are filtered out so we
    // don't pollute event property dictionaries with no-signal columns.
    // Enrichment layer order (later wins on key conflict):
    //   1. Device info (browser/os/locale/screen — captured once at boot)
    //   2. Auto-tracker session + pageview + acquisition + click IDs
    //   3. Super properties (registered once via Crossdeck.register)
    //   4. Group memberships (set via Crossdeck.group)
    //   5. Caller-supplied properties (sanitised)
    // The order is intentional: developer-supplied data is most
    // authoritative, so it overrides anything the SDK auto-attached.
    const enriched: EventProperties = { ...s.deviceInfo };
    const sessionId = s.autoTracker?.currentSessionId;
    if (sessionId) enriched.sessionId = sessionId;
    const pageviewId = s.autoTracker?.currentPageviewId;
    if (pageviewId) enriched.pageviewId = pageviewId;
    const acquisition = s.autoTracker?.currentAcquisition;
    if (acquisition) {
      // UTMs and referrer host are always attached (they're considered
      // analytics, not marketing PII). Paid-traffic click IDs and the
      // full referrer URL gate on marketing consent — the developer's
      // user said "no marketing tracking" → drop them.
      if (acquisition.utm_source) enriched.utm_source = acquisition.utm_source;
      if (acquisition.utm_medium) enriched.utm_medium = acquisition.utm_medium;
      if (acquisition.utm_campaign) enriched.utm_campaign = acquisition.utm_campaign;
      if (acquisition.utm_content) enriched.utm_content = acquisition.utm_content;
      if (acquisition.utm_term) enriched.utm_term = acquisition.utm_term;
      if (acquisition.referrer && s.consent.marketing) enriched.referrer = acquisition.referrer;
      if (s.consent.marketing) {
        if (acquisition.gclid) enriched.gclid = acquisition.gclid;
        if (acquisition.fbclid) enriched.fbclid = acquisition.fbclid;
        if (acquisition.msclkid) enriched.msclkid = acquisition.msclkid;
        if (acquisition.ttclid) enriched.ttclid = acquisition.ttclid;
        if (acquisition.li_fat_id) enriched.li_fat_id = acquisition.li_fat_id;
        if (acquisition.twclid) enriched.twclid = acquisition.twclid;
      }
    }
    // Super properties registered via Crossdeck.register(). Skipped
    // for keys the auto-enrichment already supplied so a `register`
    // call can't accidentally shadow `sessionId` etc.
    const supers = s.superProps.getSuperProperties();
    for (const k of Object.keys(supers)) {
      if (!(k in enriched)) enriched[k] = supers[k];
    }
    // Group memberships — attached as `$groups.<type>` for B2B
    // dashboards. Mixpanel uses `$groups`; we mirror exactly so
    // existing integrators don't need a translation layer.
    const groupIds = s.superProps.getGroupIds();
    if (Object.keys(groupIds).length > 0) {
      enriched.$groups = groupIds;
    }
    Object.assign(enriched, validation.properties);

    // ----- PII scrub -----
    // Last step before the event lands in the queue: defensive regex
    // scrub on URL paths, titles, and any string property value. An
    // app that puts emails or card numbers in URLs (`/users/wes@…/`)
    // would otherwise ship that PII straight to the warehouse. Even
    // with explicit consent this is the right default — Stripe scrubs
    // pre-storage too. Disable via `scrubPii: false` in init() for
    // pipelines that do their own redaction.
    const finalProperties = s.scrubPii ? scrubPiiFromProperties(enriched) : enriched;

    const event: QueuedEvent = {
      eventId: this.mintEventId(),
      name,
      timestamp: Date.now(),
      properties: finalProperties,
    };
    Object.assign(event, this.identityHintForEvent());
    s.events.enqueue(event);
  }

  /**
   * Force-flush queued events. Useful to call from page-unload handlers.
   *
   * Pass `{ keepalive: true }` from terminal handlers (pagehide /
   * visibilitychange→hidden / beforeunload). The browser keeps the
   * request alive after the page tears down, so the final batch
   * actually lands instead of being cancelled with the unload.
   *
   * NorthStar §4: standard method name across all Crossdeck SDKs.
   */
  async flush(options: { keepalive?: boolean } = {}): Promise<void> {
    const s = this.requireStarted();
    await s.events.flush(options);
  }

  /** @deprecated Use `flush()` instead. NorthStar §4 standardised the name. */
  async flushEvents(): Promise<void> {
    return this.flush();
  }

  /**
   * Forward purchase evidence to the backend for verification + entitlement
   * projection. NorthStar §4 + §13 canonical name.
   *
   * Today the web SDK only supports Apple StoreKit 2 forwarding (web apps
   * that sit alongside an iOS app). Stripe doesn't need this method —
   * Stripe webhooks deliver evidence server-side without a client round-trip.
   */
  async syncPurchases(input: {
    rail?: "apple";
    signedTransactionInfo: string;
    signedRenewalInfo?: string;
    appAccountToken?: string;
  }): Promise<PurchaseResult> {
    const s = this.requireStarted();
    if (!input.signedTransactionInfo) {
      throw new CrossdeckError({
        type: "invalid_request_error",
        code: "missing_signed_transaction_info",
        message: "syncPurchases requires a signedTransactionInfo string from StoreKit 2.",
      });
    }
    const result = await s.http.request<PurchaseResult>("POST", "/purchases/sync", {
      body: { rail: input.rail ?? "apple", ...input },
    });
    s.identity.setCrossdeckCustomerId(result.crossdeckCustomerId);
    s.entitlements.setFromList(result.entitlements);
    s.debug.emit(
      "sdk.purchase_evidence_sent",
      "StoreKit transaction forwarded. Waiting for backend verification.",
      { rail: input.rail ?? "apple" },
    );
    return result;
  }

  /** @deprecated Use `syncPurchases()` instead. NorthStar §4 standardised the name. */
  async purchaseApple(input: {
    signedTransactionInfo: string;
    signedRenewalInfo?: string;
    appAccountToken?: string;
  }): Promise<PurchaseResult> {
    return this.syncPurchases({ rail: "apple", ...input });
  }

  /**
   * Toggle verbose diagnostic logging — NorthStar §16. When enabled, the
   * SDK emits a fixed vocabulary of debug signals to console.info that the
   * dashboard's onboarding checklist can also surface as live events.
   */
  setDebugMode(enabled: boolean): void {
    const s = this.requireStarted();
    s.debug.enabled = enabled;
    if (enabled) {
      s.debug.emit(
        "sdk.configured",
        `Debug mode enabled for ${s.options.appId} in ${s.options.environment} mode.`,
        { appId: s.options.appId, environment: s.options.environment },
      );
    }
  }

  /**
   * Send the boot heartbeat. Called automatically by start() unless
   * autoHeartbeat:false. Safe to call manually as a "we're still here" ping.
   */
  async heartbeat(): Promise<HeartbeatResponse> {
    const s = this.requireStarted();
    const result = await s.http.request<HeartbeatResponse>("GET", "/sdk/heartbeat");
    // Capture clock skew at the SAME instant on both sides. The
    // `serverTime` field is the only authoritative source the SDK has
    // for "what does the backend think the time is" — used in
    // diagnostics() so a wrong-system-clock problem surfaces before
    // it silently shifts a day of analytics.
    if (typeof result?.serverTime === "number" && Number.isFinite(result.serverTime)) {
      s.lastServerTime = result.serverTime;
      s.lastClientTime = Date.now();
    }
    return result;
  }

  /**
   * Wipe persisted identity + entitlement cache. Use on logout. The
   * next pre-login session generates a fresh anonymousId and starts a
   * new identity-graph entry.
   */
  reset(): void {
    if (!this.state) return;
    // Server-derived milestone: emit `user.signed_out` BEFORE we wipe
    // identity. The track() call enqueues the event with the current
    // developerUserId/cdcust stamped on it; the subsequent reset of
    // the identity store happens locally only — the queued event
    // already left with the right identity. The dashboard's Activity
    // stream therefore shows "Wes signed out" rather than an
    // anonymous orphan event. Skipped if there's no developerUserId
    // (calling reset on a never-identified anonymous device is a no-op
    // semantically — there was nothing to "sign out" of).
    if (this.state.developerUserId) {
      try {
        this.track("user.signed_out", { auto: true });
      } catch {
        // track() throws only on invalid name — never for a literal we control.
        // Defensive catch keeps reset() bulletproof for logout flows.
      }
    }
    // Tear down + reinstall the auto-tracker so the new session belongs
    // to the new identity, not the old one. Unload-flush listeners stay
    // installed across reset — they're tied to the SDK lifetime, not
    // the identity lifetime. Web Vitals stay attached too — their
    // observers are per-page-life, not per-identity.
    this.state.autoTracker?.uninstall();
    this.state.identity.reset();
    this.state.entitlements.clear();
    this.state.events.reset();
    // Super properties + groups are identity-scoped — clear on logout
    // so a fresh anonymous session doesn't inherit the previous user's
    // plan/role/group context.
    this.state.superProps.clear();
    this.state.developerUserId = null;
    if (this.state.autoTracker) {
      const tracker = new AutoTracker(this.state.options.autoTrack, (name, props) =>
        this.track(name, props),
      );
      this.state.autoTracker = tracker;
      tracker.install();
    }
  }

  /**
   * Diagnostic: current state + queue stats. Useful for the dashboard's
   * heartbeat row and debugging in dev.
   *
   * Returns a stable shape regardless of whether start() has been called —
   * callers don't need to narrow on `started` to access `events` or
   * `entitlements`. Pre-start values are sensible empties.
   */
  diagnostics(): Diagnostics {
    if (!this.state) {
      return {
        started: false,
        anonymousId: null,
        crossdeckCustomerId: null,
        developerUserId: null,
        sdkVersion: null,
        baseUrl: null,
        clock: { lastServerTime: null, lastClientTime: null, skewMs: null },
        entitlements: { count: 0, lastUpdated: 0, listenerErrors: 0 },
        events: {
          buffered: 0,
          dropped: 0,
          inFlight: 0,
          lastFlushAt: 0,
          lastError: null,
          consecutiveFailures: 0,
          nextRetryAt: null,
        },
      };
    }
    const s = this.state;
    const skewMs =
      s.lastServerTime !== null && s.lastClientTime !== null
        ? s.lastClientTime - s.lastServerTime
        : null;
    return {
      started: true,
      anonymousId: s.identity.anonymousId,
      crossdeckCustomerId: s.identity.crossdeckCustomerId,
      developerUserId: s.developerUserId,
      sdkVersion: s.options.sdkVersion,
      baseUrl: s.options.baseUrl,
      clock: {
        lastServerTime: s.lastServerTime,
        lastClientTime: s.lastClientTime,
        skewMs,
      },
      entitlements: {
        count: s.entitlements.list().length,
        lastUpdated: s.entitlements.freshness,
        listenerErrors: s.entitlements.listenerErrors,
      },
      events: s.events.getStats(),
    };
  }

  // ---------- private helpers ----------

  private requireStarted(): InternalState {
    if (!this.state) {
      throw new CrossdeckError({
        type: "configuration_error",
        code: "not_initialized",
        message:
          "Call Crossdeck.init({ appId, publicKey, environment }) before any other method.",
      });
    }
    return this.state;
  }

  /**
   * Build the identity query for /v1/entitlements. Priority:
   *   crossdeckCustomerId > developerUserId > anonymousId
   * — matches the resolveCrossdeckCustomerId precedence on the server.
   */
  private identityQueryParams(): Record<string, string | undefined> {
    const s = this.requireStarted();
    if (s.identity.crossdeckCustomerId) {
      return { customerId: s.identity.crossdeckCustomerId };
    }
    if (s.developerUserId) return { userId: s.developerUserId };
    return { anonymousId: s.identity.anonymousId };
  }

  /**
   * Embed every known identity axis on the event. Earlier this returned
   * just the highest-priority hint (cdcust → developerUserId → anonymousId)
   * to keep payloads small, but that leaked into analytics: once a user
   * was logged in, every subsequent page.viewed shipped without
   * anonymousId, and `uniqExact(anonymous_id)` on the warehouse side
   * counted 0 visitors for the entire authenticated app.
   *
   * Bank-grade rule: the server is the single source of truth on
   * dedup. Send everything we know; let CH count by whichever axis
   * matches the question. Each field is at most 32 bytes — sending
   * three on every event costs ~80 bytes per request, which is
   * trivial compared to the analytics correctness it buys.
   */
  private identityHintForEvent(): Pick<
    QueuedEvent,
    "developerUserId" | "anonymousId" | "crossdeckCustomerId"
  > {
    const s = this.requireStarted();
    const hint: Pick<QueuedEvent, "developerUserId" | "anonymousId" | "crossdeckCustomerId"> = {
      anonymousId: s.identity.anonymousId,
    };
    if (s.developerUserId) hint.developerUserId = s.developerUserId;
    if (s.identity.crossdeckCustomerId) {
      hint.crossdeckCustomerId = s.identity.crossdeckCustomerId;
    }
    return hint;
  }

  private mintEventId(): string {
    const ts = Date.now().toString(36);
    return `evt_${ts}${randomChars(8)}`;
  }
}

/**
 * Default singleton — most consumers want one SDK instance per app.
 * Creating extra instances is fine; just `new CrossdeckClient()`.
 */
export const Crossdeck = new CrossdeckClient();

/**
 * Normalise the autoTrack option to a fully-resolved AutoTrackConfig.
 *   undefined      → all defaults (everything on in browsers)
 *   true           → all on (same as defaults)
 *   false          → all off
 *   { sessions:false } → defaults for unspecified flags, override for specified ones
 */
/**
 * Derive the env from a publishable key prefix.
 *   cd_pub_test_… → "sandbox"
 *   cd_pub_live_… → "production"
 *   cd_pub_…       → null (legacy / unprefixed — env can't be inferred)
 *
 * We treat the legacy form as "no opinion" so the developer's explicit
 * `environment` declaration always wins for unprefixed keys (e.g. dev
 * fixture keys in tests).
 */
function inferEnvFromKey(publicKey: string): Environment | null {
  if (publicKey.startsWith("cd_pub_test_")) return "sandbox";
  if (publicKey.startsWith("cd_pub_live_")) return "production";
  return null;
}

/**
 * Detect whether the SDK is booting on a local-development hostname.
 * Triggers the SDK's silent dev-mode shutoff (no network calls, all
 * state local) so a developer running their app locally with a live
 * key cannot accidentally pollute their production analytics.
 *
 * Match list:
 *   - localhost / 127.0.0.1     traditional local dev
 *   - *.local                   mDNS / Bonjour (e.g. mymac.local)
 *   - 10.x.x.x                  RFC1918 class A (private)
 *   - 192.168.x.x               RFC1918 class C (home / office LAN)
 *   - 172.16-31.x.x             RFC1918 class B
 *
 * Vercel preview URLs, Netlify branch deploys, ngrok tunnels — those
 * resolve to real domains and stay on whatever the key says. They're
 * not "local," they're "deployed under a temporary domain."
 *
 * Returns false in non-browser contexts (Node, Workers) — there's no
 * window.location to inspect, and a Node consumer that wired up the
 * SDK is presumably running server-side with a deliberate config.
 */
function isLocalHostname(): boolean {
  // Testing escape hatch — E2E suites + smoke pages need to exercise
  // the real wire shape from a non-deployed domain (Playwright runs
  // on 127.0.0.1). Setting `window.__CROSSDECK_FORCE_LIVE__ = true`
  // before init() returns false here so the SDK fires real fetches.
  // Not documented in SDK_TRUTH because it's internal-only — real
  // consumers should never set this.
  const w = (globalThis as {
    window?: { location?: { hostname?: string }; __CROSSDECK_FORCE_LIVE__?: boolean };
  }).window;
  if (w?.__CROSSDECK_FORCE_LIVE__ === true) return false;
  const hostname = w?.location?.hostname;
  if (!hostname) return false;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  if (hostname === "::1" || hostname === "[::1]") return true;
  if (hostname.endsWith(".local")) return true;
  if (/^10\./.test(hostname)) return true;
  if (/^192\.168\./.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)) return true;
  return false;
}

function resolveAutoTrack(
  input: CrossdeckOptions["autoTrack"],
): AutoTrackConfig {
  if (input === false) {
    return {
      sessions: false,
      pageViews: false,
      deviceInfo: false,
      clicks: false,
      webVitals: false,
    };
  }
  if (input === undefined || input === true) {
    return { ...DEFAULT_AUTO_TRACK };
  }
  return {
    sessions: input.sessions ?? DEFAULT_AUTO_TRACK.sessions,
    pageViews: input.pageViews ?? DEFAULT_AUTO_TRACK.pageViews,
    deviceInfo: input.deviceInfo ?? DEFAULT_AUTO_TRACK.deviceInfo,
    clicks: input.clicks ?? DEFAULT_AUTO_TRACK.clicks,
    webVitals: input.webVitals ?? DEFAULT_AUTO_TRACK.webVitals,
  };
}

/**
 * Install browser unload listeners that fire `onUnload` when the page
 * is going away. We listen to all three because each browser/platform
 * is unreliable on at least one of them:
 *   - `pagehide` is the modern, mobile-reliable signal (Safari iOS only
 *     fires this — beforeunload doesn't fire on backgrounding there).
 *   - `visibilitychange → hidden` is the canonical "tab going to bg"
 *     signal; bfcache restores re-fire `pagehide`/`pageshow`.
 *   - `beforeunload` is the legacy desktop signal — kept as a belt for
 *     older Chrome/Firefox versions.
 *
 * The handler is idempotent: if the queue is empty, flush() is a no-op,
 * so multiple firings during one unload are harmless.
 *
 * Returns a teardown that removes all three listeners. No-ops in non-
 * browser environments (Node, Web Workers).
 */
function installUnloadFlush(onUnload: () => void): () => void {
  const w = (globalThis as { window?: Window }).window;
  const doc = (globalThis as { document?: Document }).document;
  if (!w || !doc) return () => undefined;

  const onVisChange = (): void => {
    if (doc.visibilityState === "hidden") onUnload();
  };
  const onTerminal = (): void => onUnload();

  doc.addEventListener("visibilitychange", onVisChange);
  w.addEventListener("pagehide", onTerminal);
  w.addEventListener("beforeunload", onTerminal);

  return () => {
    doc.removeEventListener("visibilitychange", onVisChange);
    w.removeEventListener("pagehide", onTerminal);
    w.removeEventListener("beforeunload", onTerminal);
  };
}
